import { z } from 'zod';

import type {
  TriggerProfileCreate,
  TriggerProfileUpdate,
} from '@/lib/api/generated/model';

/**
 * The one driver this feature's UI counterpart originally targeted (backend
 * Spec 1, `063-workspace-trigger-signals`). Stored verbatim as
 * `TriggerProfileView`/`TriggerProfileCreate`'s `type` field — never renamed
 * (constitution Principle 3).
 */
export const WORKSPACE_TASK_CHANGE_TYPE = 'workspace_task_change';

/**
 * Reserved sentinel for `workspace_task_change`'s `from_step_id`/
 * `to_step_id` (spec 074, US5, backend `app.features.triggers.drivers.
 * ANY_STEP`) — matches any concrete step on whichever side names it. Scoped
 * strictly to this one driver's watched-transition fields (FR-047) — never
 * a value for a create-task/continue-task action's own `target_step_id`,
 * which always names a real step to act on.
 */
export const ANY_STEP = 'any_step';

/**
 * Mirrors `config.action.kind` — a UI-only convenience for branching a
 * form's fields. The value itself is the backend's own discriminator, not a
 * locally invented concept. Shared by every driver's action config
 * (`workspace_task_change` and, per Spec 065, `github`/`gitlab` — see
 * `ScheduledTriggerConfigSchema` below): the action family itself is the
 * one thing later specs reuse unchanged (spec.md architecture note).
 */
export type TriggerActionKind = 'create_task' | 'continue_task';

const CreateTaskActionSchema = z.object({
  kind: z.literal('create_task'),
  target_workflow_id: z.string(),
  target_step_id: z.string(),
  title_template: z.string(),
  description_template: z.string().nullable().optional(),
  input_value_templates: z.record(z.string(), z.string()).optional(),
});

const ContinueTaskActionSchema = z.object({
  kind: z.literal('continue_task'),
  comment_template: z.string(),
  source_step_id: z.string(),
  target_step_id: z.string(),
});

/**
 * The wire-shape action union — `CreateTaskAction | ContinueTaskAction`
 * (backend `data-model.md`) — shared by every driver's `config.action`.
 * Named and exported so `ScheduledTriggerConfigSchema` (research.md D1,
 * FR-009) reuses this exact schema rather than redefining an equivalent one.
 */
export const TriggerActionSchema = z.discriminatedUnion('kind', [
  CreateTaskActionSchema,
  ContinueTaskActionSchema,
]);

export type TriggerActionWire = z.infer<typeof TriggerActionSchema>;

/**
 * The activation-mode discriminant on a `workspace_task_change` profile's
 * `config` (backend `data-model.md`'s `EventModeConfig | ScheduleModeConfig`,
 * `Field(discriminator="mode")`). `event` is today's shape (a watched
 * from-step/to-step transition); `schedule` fires on a cadence instead, with
 * no watched transition at all — the same cadence fields `github`/`gitlab`
 * already send at the top level of `TriggerProfileCreate`/`TriggerProfileView`,
 * not inside `config` (Spec 082 research.md).
 */
export type WorkspaceTaskChangeMode = 'event' | 'schedule';

/**
 * The write-path shapes (create/edit form submission) for each mode —
 * `action` narrowed to `CreateTaskAction` only, mirroring the backend's own
 * narrowing (both `EventModeConfig.action` and `ScheduleModeConfig.action`
 * reject `ContinueTaskAction`; Spec 082 FR-007). `.strict()` mirrors the
 * backend's `extra="forbid"` on both Pydantic models — in particular, this
 * is what makes a schedule-mode config carrying `workflow_id`/
 * `from_step_id`/`to_step_id` an invariant of the type here too, not just on
 * the backend (data-model.md).
 */
const EventModeConfigSchema = z
  .object({
    mode: z.literal('event'),
    workflow_id: z.string(),
    from_step_id: z.string(),
    to_step_id: z.string(),
    action: CreateTaskActionSchema,
  })
  .strict();

const ScheduleModeConfigSchema = z
  .object({
    mode: z.literal('schedule'),
    action: CreateTaskActionSchema,
  })
  .strict();

/**
 * A local, non-authoritative mirror of the backend's opaque
 * `WorkspaceTaskChangeConfig` (backend `data-model.md`) — `config` is typed
 * `Record<string, JsonValue>` at the wire boundary (`TriggerProfileView`/
 * `TriggerProfileCreate`), so nothing on the generated client guarantees this
 * shape at compile time.
 *
 * Deliberately a zod schema, not a bare TypeScript type: every read of a
 * stored `config` back off the wire runs through `.safeParse()` here rather
 * than an unchecked `as` cast (constitution Principle 5; research.md D7).
 * This is the write-path schema only (`action` narrowed to `create_task`) —
 * the create/edit form validates against it before submission. It is NOT
 * used by the read-path detail view, which needs the more permissive
 * `WorkspaceTaskChangeConfigDisplaySchema` below so a legacy `continue_task`
 * profile still parses for display (Spec 082 data-model.md Validation Rule 6).
 */
export const WorkspaceTaskChangeConfigSchema = z.discriminatedUnion('mode', [
  EventModeConfigSchema,
  ScheduleModeConfigSchema,
]);

export type WorkspaceTaskChangeConfig = z.infer<
  typeof WorkspaceTaskChangeConfigSchema
>;

/**
 * The read-path shapes (detail view display) for each mode — identical to
 * the write-path shapes above except `action` stays the full
 * `TriggerActionSchema` union, so a pre-existing profile with
 * `action.kind === "continue_task"` (created before this driver's action
 * was narrowed) still parses and displays correctly (Spec 082 FR-011).
 */
const EventModeDisplayConfigSchema = z.object({
  mode: z.literal('event'),
  workflow_id: z.string(),
  from_step_id: z.string(),
  to_step_id: z.string(),
  action: TriggerActionSchema,
});

const ScheduleModeDisplayConfigSchema = z.object({
  mode: z.literal('schedule'),
  action: TriggerActionSchema,
});

/**
 * The read-path counterpart to `WorkspaceTaskChangeConfigSchema` — used only
 * by `TriggerProfileDetailSheet` (Spec 082 data-model.md). Never used for
 * write-path validation; the create/edit form always validates against the
 * narrowed `WorkspaceTaskChangeConfigSchema` above.
 */
export const WorkspaceTaskChangeConfigDisplaySchema = z.discriminatedUnion(
  'mode',
  [EventModeDisplayConfigSchema, ScheduleModeDisplayConfigSchema],
);

/**
 * Applies the same "a config with no `mode` key means event mode" default
 * the backend's `BeforeValidator` applies (`_default_missing_mode_to_event`,
 * backend `data-model.md`) — every `workspace_task_change` profile written
 * before this feature has no `mode` key at all. Must run before either
 * `WorkspaceTaskChangeConfigSchema` or `WorkspaceTaskChangeConfigDisplaySchema`
 * parses a stored config; the schemas themselves require `mode` and do not
 * default it (Spec 082 FR-010, Edge Cases).
 */
export function withDefaultedMode(config: unknown): unknown {
  if (typeof config === 'object' && config !== null && !('mode' in config)) {
    return { ...config, mode: 'event' };
  }
  return config;
}

const createTaskFormActionSchema = z.object({
  kind: z.literal('create_task'),
  targetWorkflowId: z.string().min(1, 'Choose a target workflow.'),
  targetStepId: z.string().min(1, 'Choose a target step.'),
  titleTemplate: z.string().trim().min(1, 'A title template is required.'),
  descriptionTemplate: z.string().nullable(),
  inputValueTemplates: z.record(z.string(), z.string()),
});

const continueTaskFormActionSchema = z.object({
  kind: z.literal('continue_task'),
  commentTemplate: z.string().trim().min(1, 'A comment template is required.'),
  sourceStepId: z.string().min(1, 'Choose a source step.'),
  targetStepId: z.string().min(1, 'Choose a target step.'),
});

/**
 * The action portion of a create/edit form's edited (camelCase,
 * react-hook-form) state — extracted out of what was previously
 * `TriggerProfileFormValues['action']` alone, so a second driver's form
 * (the scheduled GitHub/GitLab trigger profile, `ScheduledTriggerProfileFormValues`
 * below) can share it via `TriggerActionFields` (research.md D1/D10,
 * contracts/triggers-feature-interface-extension.md) without
 * reimplementing it (FR-009).
 */
export type TriggerActionFormValues =
  | {
      kind: 'create_task';
      targetWorkflowId: string;
      targetStepId: string;
      titleTemplate: string;
      descriptionTemplate: string | null;
      inputValueTemplates: Record<string, string>;
    }
  | {
      kind: 'continue_task';
      commentTemplate: string;
      sourceStepId: string;
      targetStepId: string;
    };

/**
 * Validates the action portion of a form's state — the shared boundary
 * check every driver's form runs via `zodResolver` (either directly, for a
 * form whose entire shape is just this, or embedded as one field of a
 * larger schema, as `triggerProfileFormSchema` does below).
 */
export const triggerActionFormSchema = z.discriminatedUnion('kind', [
  createTaskFormActionSchema,
  continueTaskFormActionSchema,
]) satisfies z.ZodType<TriggerActionFormValues>;

export const TRIGGER_ACTION_FORM_DEFAULT_VALUES: TriggerActionFormValues = {
  kind: 'create_task',
  targetWorkflowId: '',
  targetStepId: '',
  titleTemplate: '',
  descriptionTemplate: null,
  inputValueTemplates: {},
};

/**
 * Serializes the action portion of a form's edited (camelCase) shape into
 * its `TriggerActionWire` wire shape — the one place the local/wire
 * boundary is crossed for the action union, shared by every driver's own
 * `toXCreate` serializer below.
 */
export function toTriggerActionWire(
  action: TriggerActionFormValues,
): TriggerActionWire {
  return action.kind === 'create_task'
    ? {
        kind: 'create_task',
        target_workflow_id: action.targetWorkflowId,
        target_step_id: action.targetStepId,
        title_template: action.titleTemplate,
        description_template: action.descriptionTemplate,
        input_value_templates: action.inputValueTemplates,
      }
    : {
        kind: 'continue_task',
        comment_template: action.commentTemplate,
        source_step_id: action.sourceStepId,
        target_step_id: action.targetStepId,
      };
}

/**
 * Deserializes a stored `TriggerActionWire` (a profile's persisted
 * `config.action`, spec 074 US1) back into the form's edited (camelCase)
 * shape — the inverse of `toTriggerActionWire`, needed to seed an edit
 * form's `defaultValues` from an existing profile. `input_value_templates`
 * defaults to `{}` when absent, matching `TRIGGER_ACTION_FORM_DEFAULT_VALUES`'s
 * own default for a field the backend may omit entirely.
 */
export function fromTriggerActionWire(
  action: TriggerActionWire,
): TriggerActionFormValues {
  return action.kind === 'create_task'
    ? {
        kind: 'create_task',
        targetWorkflowId: action.target_workflow_id,
        targetStepId: action.target_step_id,
        titleTemplate: action.title_template,
        descriptionTemplate: action.description_template ?? null,
        inputValueTemplates: action.input_value_templates ?? {},
      }
    : {
        kind: 'continue_task',
        commentTemplate: action.comment_template,
        sourceStepId: action.source_step_id,
        targetStepId: action.target_step_id,
      };
}

/**
 * The interval/cron pairing rule (`scheduleType: "cron"` requires both
 * `cronExpression` and `timezone`; `"interval"` requires a positive
 * `intervalSeconds`), as three plain predicates rather than inlined
 * `.refine()` callbacks — enforced client-side because the backend only
 * enforces it at the application layer, not in `TriggerProfileCreate`'s
 * JSON Schema (research.md, Spec 065 digest §6). Exported as functions, not
 * duplicated `.refine()` calls, so `CreateTriggerProfileSheet`'s own nested
 * per-provider schema (its `config` sub-object needs the identical rule)
 * reuses the exact same logic rather than re-deriving it. Declared here
 * (rather than only where the scheduled GitHub/GitLab shapes are defined
 * below) so this section can use them too — `triggerProfileFormSchema`'s
 * schedule branch needs the identical rule for `workspace_task_change`
 * schedule mode (Spec 082).
 */
export function scheduleHasPositiveIntervalWhenRequired(values: {
  scheduleType: string;
  intervalSeconds: number | null;
}): boolean {
  return (
    values.scheduleType !== 'interval' ||
    (values.intervalSeconds !== null && values.intervalSeconds > 0)
  );
}

export function scheduleHasCronExpressionWhenRequired(values: {
  scheduleType: string;
  cronExpression: string | null;
}): boolean {
  return (
    values.scheduleType !== 'cron' ||
    (values.cronExpression !== null && values.cronExpression.trim() !== '')
  );
}

export function scheduleHasTimezoneWhenRequired(values: {
  scheduleType: string;
  timezone: string | null;
}): boolean {
  return (
    values.scheduleType !== 'cron' ||
    (values.timezone !== null && values.timezone.trim() !== '')
  );
}

/** The schedule-only fields shared by `workspace_task_change` schedule mode
 *  and the scheduled GitHub/GitLab shape below — declared once here so both
 *  can `.extend()`/spread the same field set (Spec 082 research.md D1's
 *  reuse rationale, applied to validation, not just JSX). */
const scheduleFieldsSchema = z.object({
  scheduleType: z.enum(['interval', 'cron']),
  intervalSeconds: z.number().nullable(),
  cronExpression: z.string().nullable(),
  timezone: z.string().nullable(),
});

/**
 * The create form's edited shape (camelCase, react-hook-form state) — built
 * to be serialized into `TriggerProfileCreate`'s wire shape
 * (`WorkspaceTaskChangeConfig` above) at submission, not sent directly.
 *
 * `mode` (Spec 082) discriminates event vs. schedule activation
 * (`WorkspaceTaskChangeMode`). `workflowId`/`fromStepId`/`toStepId` are only
 * meaningful in event mode; the schedule fields are only meaningful in
 * schedule mode — both sets are present on every value of this type (rather
 * than nested per-branch) because `TriggerProfileFormValues` predates this
 * feature as a flat interface and `CreateTriggerProfileSheet`'s own RHF
 * state already narrows by branch itself (research.md D3).
 */
export interface TriggerProfileFormValues {
  name: string;
  description: string | null;
  enabled: boolean;
  mode: WorkspaceTaskChangeMode;
  workflowId: string;
  fromStepId: string;
  toStepId: string;
  scheduleType: 'interval' | 'cron';
  intervalSeconds: number | null;
  cronExpression: string | null;
  timezone: string | null;
  action: TriggerActionFormValues;
}

/**
 * Validates `CreateTriggerProfileSheet`'s full form state (camelCase,
 * react-hook-form) — the create form's own boundary check, run via
 * `zodResolver` (the same pattern `features/tasks/taskForm.ts` establishes).
 * Distinct from `WorkspaceTaskChangeConfigSchema` above, which validates the
 * wire-shape `config` object on both the write path (after this schema, at
 * serialization) and the read path (`TriggerProfileDetailSheet`).
 *
 * A `z.discriminatedUnion('mode', ...)` (Spec 082): the event branch keeps
 * today's `workflowId`/`fromStepId`/`toStepId` requirements; the schedule
 * branch requires the schedule fields, refined with the same three
 * predicates `scheduledTriggerProfileFormSchema` uses below. Both branches'
 * `action` validates against `createTaskFormActionSchema` only, not the full
 * `triggerActionFormSchema` union — this driver's action is `create_task`
 * only in either mode (FR-007). The schedule branch's cross-field
 * refinements are applied with a single `.superRefine()` (three
 * `ctx.addIssue()` calls, one per predicate) rather than three chained
 * `.refine()` calls — Zod 4's `discriminatedUnion` accepts a
 * `.superRefine()`'d member (verified against this repo's installed zod@4.4.3),
 * unlike chained `.refine()`, so this is the form that keeps the union
 * working while still enforcing all three rules.
 */
/**
 * Both branches below carry every field `TriggerProfileFormValues` declares
 * (not just the fields meaningful in that mode) — matching the flat-interface
 * shape research.md D3 chose. The event branch's schedule fields, and the
 * schedule branch's `workflowId`/`fromStepId`/`toStepId`, are present but
 * unvalidated placeholders (whatever `CreateTriggerProfileSheet` last held
 * for them, typically the defaults) — they are never read for the inactive
 * mode and never reach `toTriggerProfileCreate`'s serialized output for it.
 */
const triggerProfileEventFormSchema = z.object({
  mode: z.literal('event'),
  name: z.string().trim().min(1, 'Name is required.'),
  description: z.string().nullable(),
  enabled: z.boolean(),
  workflowId: z.string().min(1, 'Choose a workflow.'),
  fromStepId: z.string().min(1, 'Choose a from-step.'),
  toStepId: z.string().min(1, 'Choose a to-step.'),
  ...scheduleFieldsSchema.shape,
  action: createTaskFormActionSchema,
});

const triggerProfileScheduleFormSchema = z
  .object({
    mode: z.literal('schedule'),
    name: z.string().trim().min(1, 'Name is required.'),
    description: z.string().nullable(),
    enabled: z.boolean(),
    workflowId: z.string(),
    fromStepId: z.string(),
    toStepId: z.string(),
    ...scheduleFieldsSchema.shape,
    action: createTaskFormActionSchema,
  })
  .superRefine((values, ctx) => {
    if (!scheduleHasPositiveIntervalWhenRequired(values)) {
      ctx.addIssue({
        code: 'custom',
        message: 'A positive interval is required.',
        path: ['intervalSeconds'],
      });
    }
    if (!scheduleHasCronExpressionWhenRequired(values)) {
      ctx.addIssue({
        code: 'custom',
        message: 'A cron expression is required.',
        path: ['cronExpression'],
      });
    }
    if (!scheduleHasTimezoneWhenRequired(values)) {
      ctx.addIssue({
        code: 'custom',
        message: 'A timezone is required.',
        path: ['timezone'],
      });
    }
  });

export const triggerProfileFormSchema = z.discriminatedUnion('mode', [
  triggerProfileEventFormSchema,
  triggerProfileScheduleFormSchema,
]) satisfies z.ZodType<TriggerProfileFormValues>;

export const TRIGGER_PROFILE_FORM_DEFAULT_VALUES: TriggerProfileFormValues = {
  name: '',
  description: null,
  enabled: true,
  mode: 'event',
  workflowId: '',
  fromStepId: '',
  toStepId: '',
  scheduleType: 'interval',
  intervalSeconds: null,
  cronExpression: null,
  timezone: null,
  action: TRIGGER_ACTION_FORM_DEFAULT_VALUES,
};

/**
 * Narrows a form action's wire shape to `CreateTaskAction` only, throwing if
 * given a `continue_task` action. `workspace_task_change` is `create_task`
 * only in either mode (FR-007) — `CreateTriggerProfileSheet` is responsible
 * for never letting this driver reach submission with any other action
 * selected (FR-009's reset-on-driver-change), so reaching this branch with
 * `continue_task` is a caller bug, not a recoverable input — a thrown error
 * surfaces that immediately rather than silently mis-serializing a request
 * the backend would reject anyway. Avoids an `as`-cast across the
 * `TriggerActionWire` union (constitution Principle 5).
 */
function toCreateTaskActionWire(
  action: TriggerActionFormValues,
): Extract<TriggerActionWire, { kind: 'create_task' }> {
  const wire = toTriggerActionWire(action);
  if (wire.kind !== 'create_task') {
    throw new Error(
      'workspace_task_change only supports the create_task action.',
    );
  }
  return wire;
}

/**
 * Serializes the form's edited (camelCase) shape into `TriggerProfileCreate`'s
 * wire shape — the one place the local/wire boundary is crossed (contracts/
 * ui-contracts.md's `useCreateTriggerProfile`).
 *
 * Branches on `values.mode` (Spec 082): event mode serializes exactly as
 * before this feature. Schedule mode serializes `config: { mode: "schedule",
 * action }` with no `workflow_id`/`from_step_id`/`to_step_id` (the backend's
 * `ScheduleModeConfig` is `extra="forbid"`), and populates the top-level
 * `schedule_type`/`interval_seconds`/`cron_expression`/`timezone` fields the
 * same way the scheduled GitHub/GitLab path already does
 * (`toScheduledTriggerProfileCreate` below).
 */
export function toTriggerProfileCreate(
  values: TriggerProfileFormValues,
): TriggerProfileCreate {
  if (values.mode === 'schedule') {
    const config: WorkspaceTaskChangeConfig = {
      mode: 'schedule',
      action: toCreateTaskActionWire(values.action),
    };

    return {
      name: values.name,
      description: values.description,
      type: WORKSPACE_TASK_CHANGE_TYPE,
      enabled: values.enabled,
      schedule_type: values.scheduleType,
      interval_seconds:
        values.scheduleType === 'interval' ? values.intervalSeconds : null,
      cron_expression:
        values.scheduleType === 'cron' ? values.cronExpression : null,
      timezone: values.scheduleType === 'cron' ? values.timezone : null,
      config,
    };
  }

  const config: WorkspaceTaskChangeConfig = {
    mode: 'event',
    workflow_id: values.workflowId,
    from_step_id: values.fromStepId,
    to_step_id: values.toStepId,
    action: toCreateTaskActionWire(values.action),
  };

  return {
    name: values.name,
    description: values.description,
    type: WORKSPACE_TASK_CHANGE_TYPE,
    enabled: values.enabled,
    config,
  };
}

/**
 * Serializes a `workspace_task_change` edit form's edited (camelCase) shape
 * into `TriggerProfileUpdate`'s wire shape (spec 074, FR-001/FR-002) —
 * `PATCH`'s general-edit body. No `type`/`enabled` field: driver type is
 * immutable (`TriggerProfileUpdate` has no field for it at all) and enabling/
 * disabling is a separate mutation (`useSetTriggerProfileEnabled`).
 *
 * Branches on `values.mode` (Spec 082) the same way `toTriggerProfileCreate`
 * does — a profile's activation mode is fixed at creation and never changes
 * via edit (spec 082 Assumptions), so this only ever serializes the mode the
 * form was seeded with. `action` narrows to `CreateTaskAction` only
 * (`toCreateTaskActionWire`), matching this driver's create_task-only scope
 * (FR-007) in either mode.
 */
export function toTriggerProfileUpdate(
  values: TriggerProfileFormValues,
): TriggerProfileUpdate {
  if (values.mode === 'schedule') {
    const config: WorkspaceTaskChangeConfig = {
      mode: 'schedule',
      action: toCreateTaskActionWire(values.action),
    };

    return {
      name: values.name,
      description: values.description,
      config,
    };
  }

  const config: WorkspaceTaskChangeConfig = {
    mode: 'event',
    workflow_id: values.workflowId,
    from_step_id: values.fromStepId,
    to_step_id: values.toStepId,
    action: toCreateTaskActionWire(values.action),
  };

  return {
    name: values.name,
    description: values.description,
    config,
  };
}

// ---------------------------------------------------------------------------
// Scheduled GitHub/GitLab trigger profiles (backend Spec 065, `type: "github"
// | "gitlab"`) — Spec 070 additions. These are a *sibling* shape to the
// `workspace_task_change` form above, not a superset of it (research.md
// D10's package-placement note; data-model.md): the two drivers' non-action
// fields are structurally different enough (watched-transition vs
// connection+repositories+schedule) that a single generic form would
// reintroduce the "generic dynamic-form" problem the spec rules out. Both
// share only the action portion (`TriggerActionFormValues` above).
// ---------------------------------------------------------------------------

/** The two providers a scheduled trigger profile can target. */
export type ScheduledTriggerProfileType = 'github' | 'gitlab';

/**
 * Which repository activity a scheduled profile watches (backend
 * `WatchedEvent`, data-model.md) — an empty/omitted `watch` array means
 * "watch everything" (FR-008's default), enforced by the backend, not
 * re-derived here.
 */
export const WatchedEventSchema = z.enum([
  'comment',
  'review',
  'commit',
  'status',
  'merged',
  'opened',
  'pipeline_failed',
  'conflict',
]);

export type WatchedEvent = z.infer<typeof WatchedEventSchema>;

/**
 * A local, non-authoritative mirror of the backend's opaque scheduled-driver
 * `config` shape (`GithubTriggerConfig`/`GitlabTriggerConfig`,
 * data-model.md) — same opaque-JSONB-plus-zod-schema posture
 * `WorkspaceTaskChangeConfigSchema` already established, reusing
 * `TriggerActionSchema` for `action` rather than redefining it (FR-009).
 */
export const ScheduledTriggerConfigSchema = z.object({
  repositories: z.array(z.string()).min(1),
  watch: z.array(WatchedEventSchema).optional(),
  action: TriggerActionSchema,
});

export type ScheduledTriggerConfig = z.infer<
  typeof ScheduledTriggerConfigSchema
>;

/**
 * A scheduled trigger profile's edited (camelCase, react-hook-form) shape.
 * `targetWorkflowId` scopes `TriggerActionFields`' continue-task step picker
 * for this driver (which, unlike `workspace_task_change`, has no "watched
 * workflow" of its own to bind that picker to) — it round-trips through the
 * profile's top-level `target_workflow_id` field.
 */
export interface ScheduledTriggerProfileFormValues {
  name: string;
  description: string | null;
  enabled: boolean;
  type: ScheduledTriggerProfileType;
  connectionId: string;
  repositories: string[];
  watch: WatchedEvent[];
  scheduleType: 'interval' | 'cron';
  intervalSeconds: number | null;
  cronExpression: string | null;
  timezone: string | null;
  /** Round-trips through the profile's top-level `target_workflow_id` field. */
  targetWorkflowId: string;
  action: TriggerActionFormValues;
}

/**
 * The raw (pre-refine) field-level schema, exported so a caller needing
 * only a subset of these fields — `CreateTriggerProfileSheet`'s own nested
 * per-provider `config` sub-object — can `.pick()` from it rather than
 * redeclaring each field's validator. `scheduledTriggerProfileFormSchema`
 * below is this same object with the cross-field schedule refinements
 * layered on; `.pick()` isn't available once those are applied (they turn
 * the `ZodObject` into a `ZodEffects`), which is why this raw form exists
 * as its own export rather than only living inline.
 */
export const scheduledTriggerProfileRawFieldsSchema = z.object({
  name: z.string().trim().min(1, 'Name is required.'),
  description: z.string().nullable(),
  enabled: z.boolean(),
  type: z.enum(['github', 'gitlab']),
  connectionId: z.string().min(1, 'Choose a connection.'),
  repositories: z
    .array(z.string().trim().min(1))
    .min(1, 'Choose at least one repository.'),
  watch: z.array(WatchedEventSchema),
  scheduleType: z.enum(['interval', 'cron']),
  intervalSeconds: z.number().nullable(),
  cronExpression: z.string().nullable(),
  timezone: z.string().nullable(),
  targetWorkflowId: z.string(),
  action: triggerActionFormSchema,
});

/**
 * Validates a scheduled trigger profile form's full state, reusing the
 * three predicates above.
 */
export const scheduledTriggerProfileFormSchema =
  scheduledTriggerProfileRawFieldsSchema
    .refine(scheduleHasPositiveIntervalWhenRequired, {
      message: 'A positive interval is required.',
      path: ['intervalSeconds'],
    })
    .refine(scheduleHasCronExpressionWhenRequired, {
      message: 'A cron expression is required.',
      path: ['cronExpression'],
    })
    .refine(scheduleHasTimezoneWhenRequired, {
      message: 'A timezone is required.',
      path: ['timezone'],
    }) satisfies z.ZodType<ScheduledTriggerProfileFormValues>;

export const SCHEDULED_TRIGGER_PROFILE_FORM_DEFAULT_VALUES: ScheduledTriggerProfileFormValues =
  {
    name: '',
    description: null,
    enabled: true,
    type: 'github',
    connectionId: '',
    repositories: [],
    watch: [],
    scheduleType: 'interval',
    intervalSeconds: null,
    cronExpression: null,
    timezone: null,
    targetWorkflowId: '',
    action: TRIGGER_ACTION_FORM_DEFAULT_VALUES,
  };

/**
 * Serializes a scheduled trigger profile form's edited shape into
 * `TriggerProfileCreate`'s wire shape. `watch` is omitted entirely when
 * empty rather than sent as `[]` — an empty array and an absent field both
 * mean "watch everything" server-side, but omitting is what the backend's
 * own default-handling actually expects (data-model.md), not an empty list
 * asserting a value.
 */
export function toScheduledTriggerProfileCreate(
  values: ScheduledTriggerProfileFormValues,
): TriggerProfileCreate {
  const config: ScheduledTriggerConfig = {
    repositories: values.repositories,
    ...(values.watch.length > 0 ? { watch: values.watch } : {}),
    action: toTriggerActionWire(values.action),
  };

  return {
    name: values.name,
    description: values.description,
    type: values.type,
    enabled: values.enabled,
    connection_id: values.connectionId,
    // Spec 076 US3: required by the backend when `action.kind` is
    // `continue_task` (it scopes the correlation lookup to one workflow) —
    // optional/unused for `create_task`, where it means something else
    // (backend `CreateTaskAction.target_workflow_id`, unrelated to this
    // field). Sent whenever chosen, not only for `continue_task`, since a
    // caller may switch actions after picking one — the backend's own
    // validation is authoritative on when it's required.
    target_workflow_id: values.targetWorkflowId || null,
    schedule_type: values.scheduleType,
    interval_seconds:
      values.scheduleType === 'interval' ? values.intervalSeconds : null,
    cron_expression:
      values.scheduleType === 'cron' ? values.cronExpression : null,
    timezone: values.scheduleType === 'cron' ? values.timezone : null,
    config,
  };
}

/**
 * Serializes a scheduled (`github`/`gitlab`) edit form's edited (camelCase)
 * shape into `TriggerProfileUpdate`'s wire shape (spec 074, FR-001/FR-002).
 * `connection_id`/schedule fields are top-level, mirroring `TriggerProfile`'s
 * own §5 columns exactly as the create serializer above already does — only
 * `config` (`repositories`/`watch`/`action`) is nested. No `type`/`enabled`.
 */
export function toScheduledTriggerProfileUpdate(
  values: ScheduledTriggerProfileFormValues,
): TriggerProfileUpdate {
  const config: ScheduledTriggerConfig = {
    repositories: values.repositories,
    ...(values.watch.length > 0 ? { watch: values.watch } : {}),
    action: toTriggerActionWire(values.action),
  };

  return {
    name: values.name,
    description: values.description,
    connection_id: values.connectionId,
    target_workflow_id: values.targetWorkflowId || null,
    schedule_type: values.scheduleType,
    interval_seconds:
      values.scheduleType === 'interval' ? values.intervalSeconds : null,
    cron_expression:
      values.scheduleType === 'cron' ? values.cronExpression : null,
    timezone: values.scheduleType === 'cron' ? values.timezone : null,
    config,
  };
}

// ---------------------------------------------------------------------------
// Jira (spec 074, US7/US8) — a third sibling, not folded into
// `ScheduledTriggerProfileType`/`ScheduledTriggerProfileFormValues` above:
// its filter fields (`project`/`issueType`/`status`/`labels`) are shaped
// entirely differently from GitHub/GitLab's `repositories`/`watch`, so
// reusing that shape would either carry unused fields or force an awkward
// union inside one interface. Only the connection/schedule/action portion is
// structurally identical, and is simply re-declared here rather than shared
// through inheritance — three fields duplicated once is cheaper than a
// partial-shape abstraction two sibling shapes would both have to poke
// through.
// ---------------------------------------------------------------------------

/**
 * A local, non-authoritative mirror of the backend's opaque
 * `JiraTriggerConfig` (data-model.md) — `project` is required and non-empty
 * (backend `Field(min_length=1)`); `issue_type`/`status` are each
 * independently optional filter narrowing; `labels` defaults to `[]`
 * (backend `Field(default_factory=list)`).
 */
export const JiraTriggerConfigSchema = z.object({
  project: z.string(),
  // `.nullable()`, deliberately not also `.optional()`: the backend's own
  // `JiraTriggerConfig` model always serializes these keys (`issue_type: str
  // | None = None` still dumps as `"issue_type": null`, never an omitted
  // key) — `.optional()` would make TS treat the property as possibly
  // `undefined`, which fails `TriggerProfileCreateConfig`'s `{[key: string]:
  // JsonValue}` index signature on the write path even when every actual
  // value is a concrete `string | null`.
  issue_type: z.string().nullable(),
  status: z.string().nullable(),
  labels: z.array(z.string()),
  action: TriggerActionSchema,
});

export type JiraTriggerConfig = z.infer<typeof JiraTriggerConfigSchema>;

/** A scheduled Jira trigger profile's edited (camelCase) shape — see this
 *  section's own header comment for why it's a sibling of
 *  `ScheduledTriggerProfileFormValues`, not a variant of it. */
export interface JiraTriggerProfileFormValues {
  name: string;
  description: string | null;
  enabled: boolean;
  type: 'jira';
  connectionId: string;
  project: string;
  issueType: string | null;
  status: string | null;
  labels: string[];
  scheduleType: 'interval' | 'cron';
  intervalSeconds: number | null;
  cronExpression: string | null;
  timezone: string | null;
  /** Round-trips through the profile's top-level `target_workflow_id` field,
   *  same as `ScheduledTriggerProfileFormValues.targetWorkflowId`. */
  targetWorkflowId: string;
  action: TriggerActionFormValues;
}

export const jiraTriggerProfileRawFieldsSchema = z.object({
  name: z.string().trim().min(1, 'Name is required.'),
  description: z.string().nullable(),
  enabled: z.boolean(),
  type: z.literal('jira'),
  connectionId: z.string().min(1, 'Choose a connection.'),
  project: z.string().trim().min(1, 'Project is required.'),
  issueType: z.string().nullable(),
  status: z.string().nullable(),
  labels: z.array(z.string()),
  scheduleType: z.enum(['interval', 'cron']),
  intervalSeconds: z.number().nullable(),
  cronExpression: z.string().nullable(),
  timezone: z.string().nullable(),
  targetWorkflowId: z.string(),
  action: triggerActionFormSchema,
});

/** Validates a Jira trigger profile form's full state, reusing the same
 *  three schedule predicates every scheduled driver's form applies. */
export const jiraTriggerProfileFormSchema = jiraTriggerProfileRawFieldsSchema
  .refine(scheduleHasPositiveIntervalWhenRequired, {
    message: 'A positive interval is required.',
    path: ['intervalSeconds'],
  })
  .refine(scheduleHasCronExpressionWhenRequired, {
    message: 'A cron expression is required.',
    path: ['cronExpression'],
  })
  .refine(scheduleHasTimezoneWhenRequired, {
    message: 'A timezone is required.',
    path: ['timezone'],
  }) satisfies z.ZodType<JiraTriggerProfileFormValues>;

export const JIRA_TRIGGER_PROFILE_FORM_DEFAULT_VALUES: JiraTriggerProfileFormValues =
  {
    name: '',
    description: null,
    enabled: true,
    type: 'jira',
    connectionId: '',
    project: '',
    issueType: null,
    status: null,
    labels: [],
    scheduleType: 'interval',
    intervalSeconds: null,
    cronExpression: null,
    timezone: null,
    targetWorkflowId: '',
    action: TRIGGER_ACTION_FORM_DEFAULT_VALUES,
  };

/** Serializes a Jira trigger profile form's edited shape into
 *  `TriggerProfileCreate`'s wire shape. */
export function toJiraTriggerProfileCreate(
  values: JiraTriggerProfileFormValues,
): TriggerProfileCreate {
  // `issue_type`/`status` are omitted entirely rather than sent as an
  // explicit `null` when unset — the backend's `JiraTriggerConfig` defaults
  // an absent key to `None` identically, and a directly-nullable top-level
  // `config` property cannot be typed against `TriggerProfileCreateConfig`'s
  // `{[key: string]: JsonValue}` index signature (`JsonValue`'s generated
  // `interface JsonValue {}` shape does not accept a bare `null`), the same
  // reasoning `ScheduledTriggerConfig`'s own `watch` field is conditionally
  // included rather than ever sent as `undefined`.
  const config = {
    project: values.project,
    ...(values.issueType !== null ? { issue_type: values.issueType } : {}),
    ...(values.status !== null ? { status: values.status } : {}),
    labels: values.labels,
    action: toTriggerActionWire(values.action),
  };

  return {
    name: values.name,
    description: values.description,
    type: values.type,
    enabled: values.enabled,
    connection_id: values.connectionId,
    target_workflow_id: values.targetWorkflowId || null,
    schedule_type: values.scheduleType,
    interval_seconds:
      values.scheduleType === 'interval' ? values.intervalSeconds : null,
    cron_expression:
      values.scheduleType === 'cron' ? values.cronExpression : null,
    timezone: values.scheduleType === 'cron' ? values.timezone : null,
    config,
  };
}

/** Serializes a Jira edit form's edited (camelCase) shape into
 *  `TriggerProfileUpdate`'s wire shape. No `type`/`enabled`, mirroring
 *  `toScheduledTriggerProfileUpdate`'s identical omission. */
export function toJiraTriggerProfileUpdate(
  values: JiraTriggerProfileFormValues,
): TriggerProfileUpdate {
  const config = {
    project: values.project,
    ...(values.issueType !== null ? { issue_type: values.issueType } : {}),
    ...(values.status !== null ? { status: values.status } : {}),
    labels: values.labels,
    action: toTriggerActionWire(values.action),
  };

  return {
    name: values.name,
    description: values.description,
    connection_id: values.connectionId,
    target_workflow_id: values.targetWorkflowId || null,
    schedule_type: values.scheduleType,
    interval_seconds:
      values.scheduleType === 'interval' ? values.intervalSeconds : null,
    cron_expression:
      values.scheduleType === 'cron' ? values.cronExpression : null,
    timezone: values.scheduleType === 'cron' ? values.timezone : null,
    config,
  };
}
